import React, { useState, useEffect } from 'react';
import { createPortal } from 'react-dom';
import { useNavigate } from 'react-router-dom';
import { motion, useReducedMotion } from 'framer-motion';
import { getAuth, signOut } from 'firebase/auth';
import useHandle, {
  validateHandle,
  getHandleErrorMessage,
} from '../../hooks/useHandle';
import { useUser } from '../../config/UserContext';
import logger from '../../utils/logger';
import Record from '../Icons/Record';
import styles from './HandleClaimModal.module.scss';

const CHECK = '#15803d';

const CheckIcon = () => (
  <svg
    className={styles.iconSvg}
    width="24"
    height="24"
    viewBox="0 0 24 24"
    fill="none"
    aria-hidden
  >
    <circle cx="12" cy="12" r="10" stroke={CHECK} strokeWidth="2" fill="none" />
    <path
      d="M7.5 12L10.5 15L16.5 9"
      stroke={CHECK}
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
  </svg>
);

const WarningIcon = () => (
  <svg
    className={styles.iconSvg}
    width="24"
    height="24"
    viewBox="0 0 24 24"
    fill="none"
    aria-hidden
  >
    <path
      d="M12 4L4 20h16L12 4z"
      fill="#dc2626"
      stroke="#dc2626"
      strokeWidth="1.2"
      strokeLinejoin="miter"
    />
    <path
      d="M12 10v5"
      stroke="#fafafa"
      strokeWidth="1.8"
      strokeLinecap="round"
    />
    <circle cx="12" cy="17.5" r="1" fill="#fafafa" />
  </svg>
);

const HandleClaimModal = () => {
  const prefersReducedMotion = useReducedMotion();
  const navigate = useNavigate();
  const { currentUser, setCurrentUser } = useUser();

  const handleBail = async () => {
    try {
      await signOut(getAuth());
    } catch (error) {
      logger.error('HandleClaimModal sign out failed:', error);
    }
    // New users land on this modal mid-signup (often on /settings). After
    // bailing out we want them back on the public home page, not left on an
    // authenticated-only route they can no longer use.
    navigate('/', { replace: true });
  };
  const avatarSrc = currentUser?.avatar;
  const { checking, claiming, availability, checkAvailability, claimHandle } =
    useHandle();
  const hasAutoHandle = !!currentUser?.handle;

  const [value, setValue] = useState(currentUser?.handle || '');
  const [submitError, setSubmitError] = useState(null);

  useEffect(() => {
    if (currentUser?.handle) {
      checkAvailability(currentUser.handle);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    const block = (e) => {
      if (e.key === 'Escape') e.preventDefault();
    };
    window.addEventListener('keydown', block, { capture: true });
    document.body.classList.add('no-scroll');
    return () => {
      window.removeEventListener('keydown', block, { capture: true });
      document.body.classList.remove('no-scroll');
    };
  }, []);

  const handleChange = (e) => {
    const raw = e.target.value.toLowerCase().replace(/[^a-z0-9_]/g, '');
    setValue(raw);
    setSubmitError(null);
    checkAvailability(raw);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!canSubmit) return;

    const result = await claimHandle(value);
    if (result.success) {
      setCurrentUser((prev) => ({
        ...prev,
        handle: result.handle,
        handleConfirmed: true,
      }));
    } else {
      setSubmitError(result.error);
    }
  };

  const formatError = validateHandle(value);
  const isFormatValid = value.length > 0 && !formatError;
  const isOwnHandle = value === currentUser?.handle;
  const isAvailable = isOwnHandle || availability?.available === true;
  const canSubmit = isFormatValid && isAvailable && !checking && !claiming;

  const errorMessage = (() => {
    if (!value || checking) return null;
    if (submitError) return getHandleErrorMessage(submitError);
    if (formatError) return getHandleErrorMessage(formatError);
    if (!isOwnHandle && availability?.available === false)
      return getHandleErrorMessage(availability.reason);
    return null;
  })();

  const showCheck =
    value.length > 0 &&
    !checking &&
    !errorMessage &&
    isFormatValid &&
    (isOwnHandle || availability?.available === true);

  const showWarning = !!value && !checking && !!errorMessage;

  const scrimTransition = prefersReducedMotion
    ? { duration: 0.15 }
    : { duration: 0.42, ease: [0.22, 1, 0.36, 1] };

  const cardTransition = prefersReducedMotion
    ? { duration: 0.15 }
    : {
        type: 'spring',
        stiffness: 420,
        damping: 34,
        mass: 0.85,
        delay: 0.08,
      };

  const cardInitial = prefersReducedMotion
    ? { opacity: 0 }
    : { opacity: 0, y: 14, scale: 0.988 };

  const cardAnimate = prefersReducedMotion
    ? { opacity: 1 }
    : { opacity: 1, y: 0, scale: 1 };

  return createPortal(
    <motion.div
      className={styles.overlay}
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      transition={scrimTransition}
    >
      <motion.div
        className={styles.card}
        initial={cardInitial}
        animate={cardAnimate}
        transition={cardTransition}
      >
        <div className={styles.head}>
          <div className={styles.headContent}>
            <h2 className={styles.title}>
              <span className={styles.titleBold}>
                {hasAutoHandle ? 'Introducing Handles!' : 'Welcome!'}
              </span>
            </h2>
          </div>
          <p className={styles.subtext}>
            {hasAutoHandle
              ? 'Heads up - we’ve created a handle for you so others can tag you in posts and comments. Feel free to change it now:'
              : 'Claim your handle before continuing — it’s how others will find and tag you in posts and comments.'}
          </p>
        </div>

        <form className={styles.form} onSubmit={handleSubmit} noValidate>
          <label className={styles.inputShell} htmlFor="handle-claim-input">
            {avatarSrc ? (
              <img
                className={styles.inputAvatar}
                src={avatarSrc}
                alt=""
                aria-hidden
              />
            ) : (
              <span
                className={`${styles.inputAvatar} ${styles.inputAvatarFallback}`}
                aria-hidden
              >
                <Record />
              </span>
            )}
            <span className={styles.atSign} aria-hidden>
              @
            </span>
            <input
              id="handle-claim-input"
              className={styles.input}
              type="text"
              placeholder="yourhandle"
              value={value}
              onChange={handleChange}
              maxLength={20}
              autoFocus
              autoComplete="off"
              autoCapitalize="none"
              spellCheck={false}
            />
            {(checking && value.length > 0) || showCheck || showWarning ? (
              <span
                className={styles.inputIcon}
                role={checking ? 'status' : undefined}
                aria-label={checking ? 'Checking availability' : undefined}
              >
                {checking && value.length > 0 && (
                  <span className={styles.inputSpinner} aria-hidden />
                )}
                {!checking && showCheck && <CheckIcon />}
                {!checking && showWarning && <WarningIcon />}
              </span>
            ) : null}
          </label>

          {((checking && value.length > 0) ||
            errorMessage ||
            (showCheck && !isOwnHandle)) && (
            <div className={styles.feedback} aria-live="polite">
              {checking && value.length > 0 && (
                <p className={styles.feedbackChecking}>Checking name...</p>
              )}
              {!checking && errorMessage && (
                <p className={styles.feedbackError}>{errorMessage}</p>
              )}
              {!checking && !errorMessage && showCheck && !isOwnHandle && (
                <p className={styles.feedbackSuccess}>Handle available!</p>
              )}
            </div>
          )}

          <div className={styles.rules}>
            <p>• 3–20 characters</p>
            <p>• letters, numbers, underscores</p>
            <p>• no leading or trailing underscores</p>
          </div>

          <button
            type="submit"
            className={styles.submitButton}
            disabled={!canSubmit || claiming}
          >
            {claiming
              ? 'Saving…'
              : isOwnHandle
                ? 'Confirm handle'
                : 'Claim handle'}
          </button>

          <button
            type="button"
            className={styles.logoutLink}
            onClick={handleBail}
          >
            Not ready? Sign out
          </button>
        </form>
      </motion.div>
    </motion.div>,
    document.body
  );
};

export default HandleClaimModal;
